import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  baseUrl?: string;
  image?: string;
  type?: string;
}

export const useSEO = (config: SEOConfig = {}) => {
  const { t } = useTranslation();
  const location = useLocation();
  
  const {
    title = String(t('common.siteTitle', 'PrestaSlot - Solution de réservation en ligne pour professionnels')),
    description = String(t('common.siteDescription', 'Plateforme d\'organisation en ligne pour professionnels. Gérez vos disponibilités, personnalisez vos services et recevez des réservations 24/7.')),
    keywords = String(t('common.siteKeywords', 'réservation en ligne, prise de rendez-vous, gestion de rendez-vous, agenda en ligne, prestaslot')),
    author = 'Prestaslot',
    baseUrl = 'https://app.prestaslot.com',
    image = `${baseUrl}/logo192.png`,
    type = 'website'
  } = config;
  
  // Construire l'URL canonique
  const path = location.pathname;
  const canonicalUrl = `${baseUrl}${path}`;
  
  // Détecter la langue depuis l'URL
  const pathLangMatch = location.pathname.match(/^\/([a-z]{2})/);
  const lang = pathLangMatch ? pathLangMatch[1] : 'fr';
  
  // Générer les URLs alternatives pour les langues
  const getAlternateUrl = (targetLang: string) => {
    if (pathLangMatch) {
      return `${baseUrl}${path.replace(/^\/[a-z]{2}/, `/${targetLang}`)}${location.search}`;
    }
    return `${baseUrl}/${targetLang}${path}${location.search}`;
  };
  
  // Fonction utilitaire pour mettre à jour ou créer une balise meta
  const updateMetaTag = (name: string, content: string, isProperty: boolean = false) => {
    let metaTag = isProperty 
      ? document.querySelector(`meta[property="${name}"]`)
      : document.querySelector(`meta[name="${name}"]`);
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      if (isProperty) {
        metaTag.setAttribute('property', name);
      } else {
        metaTag.setAttribute('name', name);
      }
      document.head.appendChild(metaTag);
    }
    
    metaTag.setAttribute('content', content);
  };
  
  // Fonction utilitaire pour mettre à jour ou créer une balise link
  const updateLinkTag = (rel: string, href: string, attributes: Record<string, string> = {}) => {
    let selector = `link[rel="${rel}"]`;
    
    // Ajouter des attributs supplémentaires au sélecteur si nécessaire
    Object.entries(attributes).forEach(([key, value]) => {
      selector += `[${key}="${value}"]`;
    });
    
    let linkTag = document.querySelector(selector);
    
    if (!linkTag) {
      linkTag = document.createElement('link');
      linkTag.setAttribute('rel', rel);
      Object.entries(attributes).forEach(([key, value]) => {
        linkTag!.setAttribute(key, value);
      });
      document.head.appendChild(linkTag);
    }
    
    linkTag.setAttribute('href', href);
  };
  
  useEffect(() => {
    // Mettre à jour le titre de la page
    document.title = title;

    // Mettre à jour les balises meta standard
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);

    // Mettre à jour les balises Open Graph pour les réseaux sociaux
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'PrestaSlot', true);
    updateMetaTag('og:locale', lang === 'fr' ? 'fr_FR' : 'en_US', true);

    // Mettre à jour les balises Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Mettre à jour les directives robots
    updateMetaTag('robots', 'index, follow');

    // Mettre à jour l'URL canonique
    updateLinkTag('canonical', canonicalUrl);

    // Mettre à jour les URLs alternatives pour les langues
    updateLinkTag('alternate', getAlternateUrl('fr'), { hreflang: 'fr' });
    updateLinkTag('alternate', getAlternateUrl('en'), { hreflang: 'en' });
    updateLinkTag('alternate', baseUrl, { hreflang: 'x-default' });

    // Mettre à jour la langue du document
    document.documentElement.lang = lang;
  }, [title, description, keywords, author, canonicalUrl, baseUrl, lang, image, type]);
  
  // Retourner des informations utiles si nécessaire
  return {
    canonicalUrl,
    lang,
    getAlternateUrl
  };
};